import { ROLE_DATA } from '../data/roles.js';
import { DEFAULT_NIGHT_ROLE_ORDER } from '../data/constants.js';

/**
 * Build the ordered list of role IDs to WAKE UP this night.
 * This controls ACTION COLLECTION ORDER only.
 * Resolution priority is fixed in resolveNight — never changes.
 *
 * @param {object[]} players  - current player array
 * @param {number}   night    - current night number
 * @param {boolean}  elderPenaltyActive - disables village specials when Elder died
 * @param {string[]} roleOrder - custom wake order (from settings); falls back to DEFAULT
 * @param {string[]} extraDisabledRoles - roles to forcibly exclude this call
 *   (e.g. ["shepherd"] once all sheep have been distributed — an exhaustion-based
 *   exclusion, distinct from elderPenaltyActive which is a permanent camp-wide penalty)
 */
export function buildNightQueue(
  players,
  night,
  elderPenaltyActive = false,
  roleOrder = DEFAULT_NIGHT_ROLE_ORDER,
  extraDisabledRoles = []
) {
  const aliveByRole = new Set(players.filter(p => p.alive).map(p => p.baseRoleId));
  const hasAnyWolf = players.some(p => p.alive && p.effectiveAlignment === "werewolf");

  const villageSpecialRoles = new Set([
    "seer","protector","witch","raven","bear_tamer","flute_player","fox","shepherd",
  ]);
  const extraDisabledSet = new Set(extraDisabledRoles);

  // Merge provided order with DEFAULT to catch any future roles not yet in custom order
  const mergedOrder = [
    ...roleOrder,
    ...DEFAULT_NIGHT_ROLE_ORDER.filter(r => !roleOrder.includes(r)),
  ];

  return mergedOrder.filter(roleId => {
    if (roleId === "werewolf") return hasAnyWolf;
    if (!aliveByRole.has(roleId)) return false;
    if (elderPenaltyActive && villageSpecialRoles.has(roleId)) return false;
    if (extraDisabledSet.has(roleId)) return false;
    const role = ROLE_DATA[roleId];
    if (!role) return false;
    if (role.activationTiming === "first_night_only")       return night === 1;
    if (role.activationTiming === "every_night_except_first") return night > 1;
    if (role.activationTiming === "every_x_nights")         return night > 1 && night % 2 === 0;
    if (role.activationTiming === "passive")                return false;
    if (role.activationTiming === "on_death")               return false;
    return true; // every_night
  });
}
